/* Compatibility helpers for family payloads across schema generations.
   v1 payloads carry English day names and only legacy string arrays
   (teachers / choirLeaders); v2 payloads carry Russian day strings in mixed
   casing and add structured teacherV2 / choirLeaderV2 alongside the legacy
   arrays. The views consume either shape through these helpers. */

// Any-case day string (Russian or English) -> canonical English column key.
// Live payloads carry Russian in caps ("СРЕДА", "ПОНЕДЕЛЬНИК", "ПЯТНИЦА");
// the aliases also cover abbreviations and the spelling variants a hand-typed
// sheet is likely to produce.
const DAY_ALIASES = {
  monday: 'Monday',
  mon: 'Monday',
  понедельник: 'Monday',
  пн: 'Monday',
  пон: 'Monday',
  tuesday: 'Tuesday',
  tue: 'Tuesday',
  tues: 'Tuesday',
  вторник: 'Tuesday',
  вт: 'Tuesday',
  вто: 'Tuesday',
  wednesday: 'Wednesday',
  wed: 'Wednesday',
  среда: 'Wednesday',
  ср: 'Wednesday',
  сре: 'Wednesday',
  thursday: 'Thursday',
  thu: 'Thursday',
  thur: 'Thursday',
  thurs: 'Thursday',
  четверг: 'Thursday',
  чт: 'Thursday',
  чет: 'Thursday',
  friday: 'Friday',
  fri: 'Friday',
  пятница: 'Friday',
  пт: 'Friday',
  пят: 'Friday',
  saturday: 'Saturday',
  sat: 'Saturday',
  суббота: 'Saturday',
  сб: 'Saturday',
  суб: 'Saturday',
  sunday: 'Sunday',
  sun: 'Sunday',
  воскресенье: 'Sunday',
  воскресение: 'Sunday',
  вс: 'Sunday',
  вос: 'Sunday',
};

/** Normalize a day string to its canonical English name, or null when the
 *  value is missing/unrecognized (callers must omit such blocks, not crash).
 *  Case-insensitive; ignores spaces, punctuation and a "ё"/"е" mix-up. */
export const normalizeDay = (day) => {
  if (typeof day !== 'string') return null;
  const key = day
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^a-zа-я]/g, '');
  return DAY_ALIASES[key] || null;
};

/** Sort key for a day string: Monday 0 … Sunday 6; unknown days sort last. */
export const dayOrder = (day) => {
  const i = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].indexOf(
    normalizeDay(day)
  );
  return i === -1 ? 99 : i;
};

/** Names of the people running an activity block. Prefers the legacy string
 *  arrays (teachers / choirLeaders); falls back to mapping teacherV2 /
 *  choirLeaderV2 to names; returns [] when neither is present. */
export const peopleNames = (details) => {
  if (!details || typeof details !== 'object') return [];
  if (Array.isArray(details.teachers) && details.teachers.length > 0) {
    return details.teachers;
  }
  if (Array.isArray(details.choirLeaders) && details.choirLeaders.length > 0) {
    return details.choirLeaders;
  }
  if (Array.isArray(details.teacherV2) && details.teacherV2.length > 0) {
    return details.teacherV2.map((p) => p?.teacherName).filter(Boolean);
  }
  if (Array.isArray(details.choirLeaderV2) && details.choirLeaderV2.length > 0) {
    return details.choirLeaderV2.map((p) => p?.choirLeaderName).filter(Boolean);
  }
  return [];
};

/** Canonical English day names, Monday-first — the order the week views use. */
export const WEEK_DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/** Translation key for a raw day string from either schema generation, e.g.
 *  "СРЕДА" | "Wednesday" -> "days.wednesday". Returns null for unknown days so
 *  callers can fall back to printing the raw value rather than "days.null". */
export const dayKey = (day) => {
  const canonical = normalizeDay(day);
  return canonical ? `days.${canonical.toLowerCase()}` : null;
};

/** Short-form translation key for a canonical day name, e.g. "daysShort.tuesday". */
export const dayShortKey = (day) => {
  const canonical = normalizeDay(day);
  return canonical ? `daysShort.${canonical.toLowerCase()}` : null;
};

/** The weekday name for today, or null on Sunday (outside the Mon–Sat week). */
export const todayName = () => WEEK_DAYS[(new Date().getDay() + 6) % 7] || null;

/* Two parents who share a surname read once, not twice: "Коля & Люба
   Андрущенко" instead of "Андрущенко Коля & Андрущенко Люба". Names arrive as
   "Фамилия Имя" in the live data but nothing guarantees the order, so the
   surname is taken to be whichever token the two names share, in the same
   position. Anything less clear-cut - one parent, a name that is not exactly
   two words, no shared token, or the whole name shared - returns null and the
   caller shows the names exactly as they came. */
export const jointParentNames = (name1, name2) => {
  const split = (n) => (typeof n === 'string' ? n.trim().split(/\s+/).filter(Boolean) : []);
  const a = split(name1);
  const b = split(name2);
  if (a.length !== 2 || b.length !== 2) return null;
  const same = (i) => a[i].localeCompare(b[i], undefined, { sensitivity: 'base' }) === 0;
  const sharedFirst = same(0);
  const sharedLast = same(1);
  if (sharedFirst === sharedLast) return null; // neither shared, or identical names
  const surname = sharedFirst ? a[0] : a[1];
  const given1 = sharedFirst ? a[1] : a[0];
  const given2 = sharedFirst ? b[1] : b[0];
  return { given1, given2, surname };
};

/** People running an activity block WITH their phone numbers where the
 *  payload has them: [{ name, phone }] (phone null when unknown). Prefers the
 *  structured teacherV2 / choirLeaderV2 (the only place phones live); falls
 *  back to the legacy name arrays with no phones. */
export const peopleContacts = (details) => {
  if (!details || typeof details !== 'object') return [];
  const fromV2 = (list, nameKey) =>
    Array.isArray(list)
      ? list
          .map((p) => ({ name: p?.[nameKey], phone: p?.phone ? String(p.phone) : null }))
          .filter((p) => p.name)
      : [];
  const v2 = [
    ...fromV2(details.teacherV2, 'teacherName'),
    ...fromV2(details.choirLeaderV2, 'choirLeaderName'),
  ];
  if (v2.length > 0) return v2;
  return peopleNames(details).map((name) => ({ name, phone: null }));
};

/** Digits of a phone string, with a leading US country code dropped. */
const phoneDigits = (phone) => {
  const d = String(phone || '').replace(/\D/g, '');
  return d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
};

/** "5550001111" -> "(555) 000-1111"; anything not ten digits is shown as-is. */
export const formatPhone = (phone) => {
  const d = phoneDigits(phone);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(phone || '');
};

/** tel: link for a phone string — +1 for ten-digit national numbers. */
export const telHref = (phone) => {
  const d = phoneDigits(phone);
  return `tel:${d.length === 10 ? `+1${d}` : String(phone || '').replace(/\s/g, '')}`;
};

/** The day to open the week on: today if it is one of the given days, else the
 *  next one after today, wrapping to the first — "what is next", not "Monday". */
export const nextStopFrom = (today, stops) => {
  if (!Array.isArray(stops) || stops.length === 0) return null;
  const start = WEEK_DAYS.indexOf(today);
  if (start === -1) return stops[0];
  const ordered = WEEK_DAYS.filter((d) => stops.includes(d));
  return ordered.find((d) => WEEK_DAYS.indexOf(d) >= start) || ordered[0];
};
